import { v4 as uuidv4 } from 'uuid';

// Fund types
export interface Fund {
  id: string;
  name: string;
  totalCommitments: number;
  capitalCalled: number;
  distributions: number;
  residualNAV: number;
  irr: number;
  moic: number;
  dpi: number;
  tvpi: number;
  vintage: number;
  status: FundStatus;
}

export enum FundStatus {
  Fundraising = 'fundraising',
  Investing = 'investing',
  HarvestingReturns = 'harvesting_returns',
  Closed = 'closed',
}

// LP types
export interface LP {
  id: string;
  name: string;
  type: LPType;
  commitment: number;
  contributions: number;
  distributions: number;
  unrealizedNAV: number;
  preferredReturn: number;
  carryPercentage: number;
  irr: number;
  fundId: string;
}

export enum LPType {
  PensionFund = 'pension_fund',
  Endowment = 'endowment',
  FoundationFund = 'foundation_fund',
  FamilyOffice = 'family_office',
  InsuranceCompany = 'insurance_company',
  SovereignWealthFund = 'sovereign_wealth_fund',
  FundOfFunds = 'fund_of_funds',
  Individual = 'individual',
}

// NAV Attribution types
export interface NAVAttribution {
  id: string;
  fundId: string;
  date: string;
  totalNAV: number;
  components: NAVComponent[];
  commentary: string;
}

export interface NAVComponent {
  id: string;
  name: NAVComponentType;
  value: number;
  percentageOfTotal: number;
}

export enum NAVComponentType {
  NOI = 'noi',
  CapEx = 'capex',
  Revaluation = 'revaluation',
  WriteDowns = 'write_downs',
  Other = 'other',
}

// Capital Events Timeline
export interface CapitalEvent {
  id: string;
  fundId: string;
  name: string;
  type: CapitalEventType;
  date: string;
  amount: number;
  description: string;
}

export enum CapitalEventType {
  FundLaunch = 'fund_launch',
  CapitalCall = 'capital_call',
  FundRealization = 'fund_realization',
  Distribution = 'distribution',
  WriteDown = 'write_down',
}

// LP Commitment types
export interface LPCommitment {
  id: string;
  lpId: string;
  fundId: string;
  status: CommitmentStatus;
  amount: number;
  date: string;
  sideLetterTerms: SideLetterTerm[];
  tags: string[];
}

export interface SideLetterTerm {
  id: string;
  type: SideLetterTermType;
  description: string;
}

export enum CommitmentStatus {
  SoftCircled = 'soft_circled',
  Committed = 'committed',
  InDiligence = 'in_diligence',
}

export enum SideLetterTermType {
  CoInvestRights = 'co_invest_rights',
  CustomReports = 'custom_reports',
  MFNClause = 'mfn_clause',
  FeeDiscount = 'fee_discount',
  Other = 'other',
}

// Mock data for funds
export const mockFundDetails: Fund[] = [
  {
    id: uuidv4(),
    name: 'Fund I',
    totalCommitments: 250000000,
    capitalCalled: 225000000,
    distributions: 175000000,
    residualNAV: 150000000,
    irr: 15.2,
    moic: 1.8,
    dpi: 0.78,
    tvpi: 1.44,
    vintage: 2018,
    status: FundStatus.HarvestingReturns,
  },
  {
    id: uuidv4(),
    name: 'Fund II',
    totalCommitments: 500000000,
    capitalCalled: 350000000,
    distributions: 100000000,
    residualNAV: 400000000,
    irr: 18.5,
    moic: 1.43,
    dpi: 0.29,
    tvpi: 1.43,
    vintage: 2021,
    status: FundStatus.Investing,
  },
  {
    id: uuidv4(),
    name: 'Fund III',
    totalCommitments: 750000000,
    capitalCalled: 150000000,
    distributions: 0,
    residualNAV: 175000000,
    irr: 10.8,
    moic: 1.17,
    dpi: 0,
    tvpi: 1.17,
    vintage: 2023,
    status: FundStatus.Investing,
  },
  {
    id: uuidv4(),
    name: 'Fund IV',
    totalCommitments: 350000000,
    capitalCalled: 0,
    distributions: 0,
    residualNAV: 0,
    irr: 0,
    moic: 0,
    dpi: 0,
    tvpi: 0,
    vintage: 2024,
    status: FundStatus.Fundraising,
  },
];

// Mock data for LPs
export const mockLPs: LP[] = [
  // Fund I LPs
  {
    id: uuidv4(),
    name: 'California State Teachers Retirement System',
    type: LPType.PensionFund,
    commitment: 50000000,
    contributions: 45000000,
    distributions: 35000000,
    unrealizedNAV: 30000000,
    preferredReturn: 8,
    carryPercentage: 20,
    irr: 15.8,
    fundId: mockFundDetails[0].id,
  },
  {
    id: uuidv4(),
    name: 'Harvard University Endowment',
    type: LPType.Endowment,
    commitment: 25000000,
    contributions: 22500000,
    distributions: 17500000,
    unrealizedNAV: 15000000,
    preferredReturn: 8,
    carryPercentage: 20,
    irr: 14.9,
    fundId: mockFundDetails[0].id,
  },
  {
    id: uuidv4(),
    name: 'Rockefeller Foundation',
    type: LPType.FoundationFund,
    commitment: 20000000,
    contributions: 18000000,
    distributions: 14000000,
    unrealizedNAV: 12000000,
    preferredReturn: 8,
    carryPercentage: 20,
    irr: 15.3,
    fundId: mockFundDetails[0].id,
  },
  
  // Fund II LPs
  {
    id: uuidv4(),
    name: 'New York State Common Retirement Fund',
    type: LPType.PensionFund,
    commitment: 75000000,
    contributions: 52500000,
    distributions: 15000000,
    unrealizedNAV: 60000000,
    preferredReturn: 8,
    carryPercentage: 20,
    irr: 18.2,
    fundId: mockFundDetails[1].id,
  },
  {
    id: uuidv4(),
    name: 'Yale University Endowment',
    type: LPType.Endowment,
    commitment: 50000000,
    contributions: 35000000,
    distributions: 10000000,
    unrealizedNAV: 40000000,
    preferredReturn: 8,
    carryPercentage: 20,
    irr: 17.9,
    fundId: mockFundDetails[1].id,
  },
  {
    id: uuidv4(),
    name: 'Gates Foundation',
    type: LPType.FoundationFund,
    commitment: 40000000,
    contributions: 28000000,
    distributions: 8000000,
    unrealizedNAV: 32000000,
    preferredReturn: 8,
    carryPercentage: 20,
    irr: 18.7,
    fundId: mockFundDetails[1].id,
  },
  
  // Fund III LPs
  {
    id: uuidv4(),
    name: 'Texas Teachers Retirement System',
    type: LPType.PensionFund,
    commitment: 100000000,
    contributions: 20000000,
    distributions: 0,
    unrealizedNAV: 23000000,
    preferredReturn: 8,
    carryPercentage: 20,
    irr: 11.2,
    fundId: mockFundDetails[2].id,
  },
  {
    id: uuidv4(),
    name: 'Stanford University Endowment',
    type: LPType.Endowment,
    commitment: 75000000,
    contributions: 15000000,
    distributions: 0,
    unrealizedNAV: 17500000,
    preferredReturn: 8,
    carryPercentage: 20,
    irr: 10.5,
    fundId: mockFundDetails[2].id,
  },
  
  // Fund IV LPs (in fundraising)
  {
    id: uuidv4(),
    name: 'Florida State Board of Administration',
    type: LPType.PensionFund,
    commitment: 80000000,
    contributions: 0,
    distributions: 0,
    unrealizedNAV: 0,
    preferredReturn: 8,
    carryPercentage: 20,
    irr: 0,
    fundId: mockFundDetails[3].id,
  },
  {
    id: uuidv4(),
    name: 'Princeton University Endowment',
    type: LPType.Endowment,
    commitment: 50000000,
    contributions: 0,
    distributions: 0,
    unrealizedNAV: 0,
    preferredReturn: 8,
    carryPercentage: 20,
    irr: 0,
    fundId: mockFundDetails[3].id,
  },
];

// Mock data for NAV Attribution
export const mockNAVAttributions: NAVAttribution[] = [
  // Fund I NAV Attribution
  {
    id: uuidv4(),
    fundId: mockFundDetails[0].id,
    date: '2023-12-31',
    totalNAV: 150000000,
    components: [
      {
        id: uuidv4(),
        name: NAVComponentType.NOI,
        value: 90000000,
        percentageOfTotal: 60,
      },
      {
        id: uuidv4(),
        name: NAVComponentType.CapEx,
        value: 15000000,
        percentageOfTotal: 10,
      },
      {
        id: uuidv4(),
        name: NAVComponentType.Revaluation,
        value: 52500000,
        percentageOfTotal: 35,
      },
      {
        id: uuidv4(),
        name: NAVComponentType.WriteDowns,
        value: -7500000,
        percentageOfTotal: -5,
      },
    ],
    commentary: 'Strong NOI growth in multifamily assets offset by retail write-downs.',
  },
  
  // Fund II NAV Attribution
  {
    id: uuidv4(),
    fundId: mockFundDetails[1].id,
    date: '2023-12-31',
    totalNAV: 400000000,
    components: [
      {
        id: uuidv4(),
        name: NAVComponentType.NOI,
        value: 200000000,
        percentageOfTotal: 50,
      },
      {
        id: uuidv4(),
        name: NAVComponentType.CapEx,
        value: 40000000,
        percentageOfTotal: 10,
      },
      {
        id: uuidv4(),
        name: NAVComponentType.Revaluation,
        value: 160000000,
        percentageOfTotal: 40,
      },
      {
        id: uuidv4(),
        name: NAVComponentType.WriteDowns,
        value: 0,
        percentageOfTotal: 0,
      },
    ],
    commentary: 'Significant cap rate compression in industrial assets driving revaluation gains.',
  },
  
  // Fund III NAV Attribution
  {
    id: uuidv4(),
    fundId: mockFundDetails[2].id,
    date: '2023-12-31',
    totalNAV: 175000000,
    components: [
      {
        id: uuidv4(),
        name: NAVComponentType.NOI,
        value: 87500000,
        percentageOfTotal: 50,
      },
      {
        id: uuidv4(),
        name: NAVComponentType.CapEx,
        value: 35000000,
        percentageOfTotal: 20,
      },
      {
        id: uuidv4(),
        name: NAVComponentType.Revaluation,
        value: 52500000,
        percentageOfTotal: 30,
      },
      {
        id: uuidv4(),
        name: NAVComponentType.WriteDowns,
        value: 0,
        percentageOfTotal: 0,
      },
    ],
    commentary: 'Early performance in line with expectations. CapEx deployment on schedule.',
  },
];

// Mock data for Capital Events
export const mockCapitalEvents: CapitalEvent[] = [
  // Fund I Capital Events
  {
    id: uuidv4(),
    fundId: mockFundDetails[0].id,
    name: 'Fund I Launch',
    type: CapitalEventType.FundLaunch,
    date: '2018-03-15',
    amount: 250000000,
    description: 'Initial closing of Fund I with $250M in commitments',
  },
  {
    id: uuidv4(),
    fundId: mockFundDetails[0].id,
    name: 'Initial Capital Call',
    type: CapitalEventType.CapitalCall,
    date: '2018-05-01',
    amount: 75000000,
    description: 'First capital call for Fund I (30%)',
  },
  {
    id: uuidv4(),
    fundId: mockFundDetails[0].id,
    name: 'Second Capital Call',
    type: CapitalEventType.CapitalCall,
    date: '2019-02-15',
    amount: 75000000,
    description: 'Second capital call for Fund I (30%)',
  },
  {
    id: uuidv4(),
    fundId: mockFundDetails[0].id,
    name: 'Third Capital Call',
    type: CapitalEventType.CapitalCall,
    date: '2020-01-10',
    amount: 75000000,
    description: 'Final capital call for Fund I (30%)',
  },
  {
    id: uuidv4(),
    fundId: mockFundDetails[0].id,
    name: 'First Distribution',
    type: CapitalEventType.Distribution,
    date: '2020-06-30',
    amount: 50000000,
    description: 'First distribution from Fund I',
  },
  {
    id: uuidv4(),
    fundId: mockFundDetails[0].id,
    name: 'Second Distribution',
    type: CapitalEventType.Distribution,
    date: '2021-12-15',
    amount: 75000000,
    description: 'Second distribution from Fund I',
  },
  {
    id: uuidv4(),
    fundId: mockFundDetails[0].id,
    name: 'Third Distribution',
    type: CapitalEventType.Distribution,
    date: '2022-09-30',
    amount: 50000000,
    description: 'Third distribution from Fund I',
  },
  
  // Fund II Capital Events
  {
    id: uuidv4(),
    fundId: mockFundDetails[1].id,
    name: 'Fund II Launch',
    type: CapitalEventType.FundLaunch,
    date: '2021-01-15',
    amount: 500000000,
    description: 'Initial closing of Fund II with $500M in commitments',
  },
  {
    id: uuidv4(),
    fundId: mockFundDetails[1].id,
    name: 'Initial Capital Call',
    type: CapitalEventType.CapitalCall,
    date: '2021-03-01',
    amount: 150000000,
    description: 'First capital call for Fund II (30%)',
  },
  {
    id: uuidv4(),
    fundId: mockFundDetails[1].id,
    name: 'Second Capital Call',
    type: CapitalEventType.CapitalCall,
    date: '2022-02-15',
    amount: 100000000,
    description: 'Second capital call for Fund II (20%)',
  },
  {
    id: uuidv4(),
    fundId: mockFundDetails[1].id,
    name: 'Third Capital Call',
    type: CapitalEventType.CapitalCall,
    date: '2023-01-10',
    amount: 100000000,
    description: 'Third capital call for Fund II (20%)',
  },
  {
    id: uuidv4(),
    fundId: mockFundDetails[1].id,
    name: 'First Distribution',
    type: CapitalEventType.Distribution,
    date: '2022-12-15',
    amount: 50000000,
    description: 'First distribution from Fund II',
  },
  {
    id: uuidv4(),
    fundId: mockFundDetails[1].id,
    name: 'Second Distribution',
    type: CapitalEventType.Distribution,
    date: '2023-09-30',
    amount: 50000000,
    description: 'Second distribution from Fund II',
  },
  
  // Fund III Capital Events
  {
    id: uuidv4(),
    fundId: mockFundDetails[2].id,
    name: 'Fund III Launch',
    type: CapitalEventType.FundLaunch,
    date: '2023-02-01',
    amount: 750000000,
    description: 'Initial closing of Fund III with $750M in commitments',
  },
  {
    id: uuidv4(),
    fundId: mockFundDetails[2].id,
    name: 'Initial Capital Call',
    type: CapitalEventType.CapitalCall,
    date: '2023-04-15',
    amount: 150000000,
    description: 'First capital call for Fund III (20%)',
  },
  
  // Fund IV Capital Events
  {
    id: uuidv4(),
    fundId: mockFundDetails[3].id,
    name: 'Fund IV Launch',
    type: CapitalEventType.FundLaunch,
    date: '2024-01-15',
    amount: 350000000,
    description: 'Initial closing of Fund IV with $350M in commitments',
  },
];

// Mock data for LP Commitments
export const mockLPCommitments: LPCommitment[] = [
  // Fund IV LP Commitments (in fundraising)
  {
    id: uuidv4(),
    lpId: mockLPs[8].id, // Florida State Board of Administration
    fundId: mockFundDetails[3].id,
    status: CommitmentStatus.Committed,
    amount: 80000000,
    date: '2024-01-15',
    sideLetterTerms: [
      {
        id: uuidv4(),
        type: SideLetterTermType.CoInvestRights,
        description: 'Right to co-invest up to $20M per deal',
      },
      {
        id: uuidv4(),
        type: SideLetterTermType.CustomReports,
        description: 'Quarterly ESG reporting',
      },
    ],
    tags: ['Re-up', 'Tier 1', 'US'],
  },
  {
    id: uuidv4(),
    lpId: mockLPs[9].id, // Princeton University Endowment
    fundId: mockFundDetails[3].id,
    status: CommitmentStatus.Committed,
    amount: 50000000,
    date: '2024-01-15',
    sideLetterTerms: [
      {
        id: uuidv4(),
        type: SideLetterTermType.MFNClause,
        description: 'Most favored nation clause',
      },
    ],
    tags: ['Re-up', 'Tier 1', 'US'],
  },
  {
    id: uuidv4(),
    lpId: uuidv4(), // New LP not in the system yet
    fundId: mockFundDetails[3].id,
    status: CommitmentStatus.SoftCircled,
    amount: 60000000,
    date: '2024-02-01',
    sideLetterTerms: [],
    tags: ['New LP', 'Tier 2', 'Europe'],
  },
  {
    id: uuidv4(),
    lpId: uuidv4(), // New LP not in the system yet
    fundId: mockFundDetails[3].id,
    status: CommitmentStatus.InDiligence,
    amount: 40000000,
    date: '2024-02-15',
    sideLetterTerms: [],
    tags: ['New LP', 'Tier 2', 'Asia'],
  },
];

// Investment Allocation data
export interface InvestmentAllocation {
  id: string;
  fundId: string;
  assetClass: string;
  percentage: number;
}

export const mockInvestmentAllocations: InvestmentAllocation[] = [
  // Fund I Allocations
  {
    id: uuidv4(),
    fundId: mockFundDetails[0].id,
    assetClass: 'Multifamily',
    percentage: 40,
  },
  {
    id: uuidv4(),
    fundId: mockFundDetails[0].id,
    assetClass: 'Industrial',
    percentage: 25,
  },
  {
    id: uuidv4(),
    fundId: mockFundDetails[0].id,
    assetClass: 'Office',
    percentage: 20,
  },
  {
    id: uuidv4(),
    fundId: mockFundDetails[0].id,
    assetClass: 'Retail',
    percentage: 15,
  },
  
  // Fund II Allocations
  {
    id: uuidv4(),
    fundId: mockFundDetails[1].id,
    assetClass: 'Multifamily',
    percentage: 45,
  },
  {
    id: uuidv4(),
    fundId: mockFundDetails[1].id,
    assetClass: 'Industrial',
    percentage: 30,
  },
  {
    id: uuidv4(),
    fundId: mockFundDetails[1].id,
    assetClass: 'Office',
    percentage: 15,
  },
  {
    id: uuidv4(),
    fundId: mockFundDetails[1].id,
    assetClass: 'Retail',
    percentage: 10,
  },
  
  // Fund III Allocations
  {
    id: uuidv4(),
    fundId: mockFundDetails[2].id,
    assetClass: 'Multifamily',
    percentage: 50,
  },
  {
    id: uuidv4(),
    fundId: mockFundDetails[2].id,
    assetClass: 'Industrial',
    percentage: 35,
  },
  {
    id: uuidv4(),
    fundId: mockFundDetails[2].id,
    assetClass: 'Office',
    percentage: 10,
  },
  {
    id: uuidv4(),
    fundId: mockFundDetails[2].id,
    assetClass: 'Retail',
    percentage: 5,
  },
];

// Cash Flow Curve data
export interface CashFlowPoint {
  id: string;
  fundId: string;
  year: number;
  contributions: number;
  distributions: number;
  nav: number;
}

export const mockCashFlowCurve: CashFlowPoint[] = [
  // Fund I Cash Flow
  {
    id: uuidv4(),
    fundId: mockFundDetails[0].id,
    year: 2018,
    contributions: 75000000,
    distributions: 0,
    nav: 70000000,
  },
  {
    id: uuidv4(),
    fundId: mockFundDetails[0].id,
    year: 2019,
    contributions: 150000000,
    distributions: 0,
    nav: 140000000,
  },
  {
    id: uuidv4(),
    fundId: mockFundDetails[0].id,
    year: 2020,
    contributions: 225000000,
    distributions: 50000000,
    nav: 200000000,
  },
  {
    id: uuidv4(),
    fundId: mockFundDetails[0].id,
    year: 2021,
    contributions: 225000000,
    distributions: 125000000,
    nav: 175000000,
  },
  {
    id: uuidv4(),
    fundId: mockFundDetails[0].id,
    year: 2022,
    contributions: 225000000,
    distributions: 175000000,
    nav: 160000000,
  },
  {
    id: uuidv4(),
    fundId: mockFundDetails[0].id,
    year: 2023,
    contributions: 225000000,
    distributions: 175000000,
    nav: 150000000,
  },
  
  // Fund II Cash Flow
  {
    id: uuidv4(),
    fundId: mockFundDetails[1].id,
    year: 2021,
    contributions: 150000000,
    distributions: 0,
    nav: 145000000,
  },
  {
    id: uuidv4(),
    fundId: mockFundDetails[1].id,
    year: 2022,
    contributions: 250000000,
    distributions: 50000000,
    nav: 275000000,
  },
  {
    id: uuidv4(),
    fundId: mockFundDetails[1].id,
    year: 2023,
    contributions: 350000000,
    distributions: 100000000,
    nav: 400000000,
  },
  
  // Fund III Cash Flow
  {
    id: uuidv4(),
    fundId: mockFundDetails[2].id,
    year: 2023,
    contributions: 150000000,
    distributions: 0,
    nav: 175000000,
  },
];
